import { Fragment, ReactNode } from "react";
import classNames from "classnames";
import styles from "./IconList.module.scss";

export interface IconProps {
    index: number;
}

export interface AggregatedIconProps {
    aggregatedCount: number;
}

export type RenderItem<TItem> = (item: TItem, props: IconProps) => ReactNode;

export type RenderAggregated<TItem> = (
    items: TItem[],
    props: AggregatedIconProps,
) => ReactNode;

export interface IconListProps<TItem extends object = object> {
    items: TItem[];
    maxItems?: number;
    renderItem: RenderItem<TItem>;
    renderAggregated?: RenderAggregated<TItem>;
    appendContent?: ReactNode;
    prependContent?: ReactNode;
    className?: string;
}

export const IconList = <TItem extends object>({
    prependContent,
    appendContent,
    items,
    maxItems = 4,
    renderItem,
    renderAggregated,
    className,
}: IconListProps<TItem>) => {
    const shouldAggregate = items.length > maxItems;

    if (shouldAggregate) {
        const individualItems = items.slice(0, maxItems - 1);
        const otherItems = items.slice(maxItems - 1);

        return (
            <span className={classNames(styles.list, className)}>
                {prependContent}
                {individualItems.map((item, index) => (
                    <Fragment key={index}>
                        {renderItem(item, { index })}
                    </Fragment>
                ))}

                {renderAggregated?.(otherItems, {
                    aggregatedCount: items.length - individualItems.length,
                })}
                {appendContent}
            </span>
        );
    } else {
        return (
            <span className={classNames(styles.list, className)}>
                {prependContent}
                {items.map((item, index) => (
                    <Fragment key={index}>
                        {renderItem(item, { index })}
                    </Fragment>
                ))}
                {appendContent}
            </span>
        );
    }
};
